import _extends from "@babel/runtime/helpers/esm/extends";
import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";
import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
var _excluded = ["id", "name", "onChange", "value", "label", "helperText", "onBlur", "icon", "$css", "disabled", "className", "checked"];
import React, { useState } from 'react';
import styled, { css } from 'styled-components';
import { COLORS, TYPOGRAPHY } from '../../../../themes';
import { flex, hoverMultiple, hoverChild, hoverChildMultiple, isKeyboard, focusStyles, combineCss, highContrastOutline } from '../../../../styling';
import { Div, Span } from '../../../Styled';
import { HelperText as BaseHelperText } from '../../../text';
import { IS_DEV } from '../../../../constants';

/**
 * TODO: Create accessible base radio button card and export. Refactor similar radios to use it.
 * Basic Pattern (works with desktop and mobile device screen readers):
 * A container (ItemContainer) manages state and border styles
 * An input (Input) is absolutely positioned within the container.
 * A label makes the entire 'card' clickable.
 */
var Input = /*#__PURE__*/styled.input.withConfig({
  displayName: "CardSelect__Input",
  componentId: "sc-s8alh2-0"
})(["width:0;height:0;margin:0;position:absolute;"]);
var Label = /*#__PURE__*/styled.label.withConfig({
  displayName: "CardSelect__Label",
  componentId: "sc-s8alh2-1"
})(["user-select:none;cursor:pointer;", ";", ";min-height:44px;text-align:center;> * + *{margin-top:8px;}"], flex('center', 'center', 'column'), TYPOGRAPHY.H6);
var HelperText = /*#__PURE__*/styled(BaseHelperText).withConfig({
  displayName: "CardSelect__HelperText",
  componentId: "sc-s8alh2-2"
})(["line-height:1.54;"]); // TODO: Update hover functions to include :checked. This would eliminate the need to use the ternary for the base.
// Checked styles currently must be added this way in order to make them work on mobile devices.

var ItemContainer = /*#__PURE__*/styled(Div).withConfig({
  displayName: "CardSelect__ItemContainer",
  componentId: "sc-s8alh2-3"
})(["position:relative;border-radius:10px;", ";", ";", ";&.storybook-focus{border-color:", ";", ";}", ";", ";"], function (_ref) {
  var $checked = _ref.$checked;
  return css(["", ";", ";", ""], hoverMultiple([{
    prop: 'border',
    base: $checked ? "2px solid ".concat(COLORS.PRIMARY_550) : "1px solid ".concat(COLORS.NEUTRAL_250),
    hover: "2px solid ".concat(COLORS.PRIMARY_550)
  }, {
    prop: 'background-color',
    base: $checked ? COLORS.PRIMARY_100 : COLORS.WHITE,
    hover: COLORS.PRIMARY_100
  }]), hoverChild("".concat(HelperText), 'color', $checked ? COLORS.PRIMARY_600 : COLORS.NEUTRAL_600, COLORS.PRIMARY_600), hoverChildMultiple("".concat(Label), [{
    prop: 'color',
    base: $checked ? COLORS.PRIMARY_600 : COLORS.NEUTRAL_550,
    hover: COLORS.PRIMARY_600
  }, {
    prop: 'padding',
    base: $checked ? '23px' : '24px',
    hover: '23px'
  }]));
}, function (_ref2) {
  var $checked = _ref2.$checked;
  return $checked && css(["", ";"], highContrastOutline);
}, function (_ref3) {
  var $focus = _ref3.$focus;
  return $focus && css(["border-color:", ";", ";"], COLORS.PRIMARY_550, focusStyles);
}, COLORS.PRIMARY_550, focusStyles, function (_ref4) {
  var $disabled = _ref4.$disabled;
  return $disabled && css(["opacity:40%;pointer-events:none;"]);
}, combineCss());

var CardSelect = function CardSelect(_ref5) {
  var id = _ref5.id,
      name = _ref5.name,
      _onChange = _ref5.onChange,
      value = _ref5.value,
      label = _ref5.label,
      helperText = _ref5.helperText,
      _onBlur = _ref5.onBlur,
      icon = _ref5.icon,
      $css = _ref5.$css,
      disabled = _ref5.disabled,
      className = _ref5.className,
      _ref5$checked = _ref5.checked,
      checked = _ref5$checked === void 0 ? false : _ref5$checked,
      rest = _objectWithoutProperties(_ref5, _excluded);

  if (IS_DEV && !label && !helperText) {
    console.error("CardSelector requires label and/or helperText");
  }

  var _useState = useState(false),
      _useState2 = _slicedToArray(_useState, 2),
      focused = _useState2[0],
      setFocused = _useState2[1];

  return /*#__PURE__*/React.createElement(ItemContainer, {
    $focus: focused,
    $css: $css,
    $disabled: disabled,
    className: className,
    $checked: checked
  }, /*#__PURE__*/React.createElement(Label, {
    htmlFor: id,
    "data-test-id": "".concat(id, "-label")
  }, icon && icon, label && /*#__PURE__*/React.createElement(Span, null, label), helperText && /*#__PURE__*/React.createElement(HelperText, null, helperText)), /*#__PURE__*/React.createElement(Input, _extends({
    type: "radio",
    id: id,
    "data-test-id": id,
    name: name,
    value: value,
    onFocus: function onFocus(e) {
      setFocused(isKeyboard(e));
    },
    onBlur: function onBlur(e) {
      if (_onBlur) _onBlur(e);
      setFocused(false);
    },
    onChange: function onChange(e) {
      if (_onChange) {
        _onChange(e);
      }
    },
    disabled: disabled,
    checked: checked
  }, rest)));
};

export default CardSelect;